import { ExtensionMessage } from '@/types';
import toast from 'react-hot-toast';

// Utility functions for communicating with the Instavault browser extension

// Check if the Instavault extension is loaded and active
export function isInstavaultExtensionLoaded() {
    // Check for the instavault-extension-installed cookie
    const cookies = document.cookie.split(';');
    const extensionCookie = cookies.find(cookie => 
        cookie.trim().startsWith('instavault-extension-installed=')
    );
    
    if (extensionCookie) {
        // Extract the value and check if it's truthy
        const cookieValue = extensionCookie.split('=')[1];
        return cookieValue === 'true';
    }
    
    return false;
}

// Function to send login data to the Instavault extension
export function sendLoginToExtension(loginData: {
    name?: string;
    accessToken: string;
    refreshToken?: string;
    userId: string;
    email: string;
    expiresAt: number;
}) {
    // Check if the extension is installed and active
    if (typeof window !== 'undefined' && isInstavaultExtensionLoaded()) {

        // Prepare the login data
        const message: ExtensionMessage = {
            action: 'loginSuccess',
            data: {
                accessToken: loginData.accessToken,
                refreshToken: loginData.refreshToken,
                userId: loginData.userId,
                email: loginData.email,
                name:loginData.name,
                expiresAt: loginData.expiresAt // Default 24 hours
            },
            source: 'instavault-website'
        };

        // Send message to the extension
        window.postMessage(message, '*');

        // Listen for confirmation from the extension
        const handleExtensionResponse = (event: MessageEvent<ExtensionMessage>) => {
            if (event.data &&
                event.data.source === 'instavault-extension' &&
                event.data.action === 'loginStored') {

                if (event.data.success) {
                    console.log('✅ Login data successfully stored in Instavault extension');
                    // Dispatch custom event to notify sync completion
                    window.dispatchEvent(new CustomEvent('instavault-extension-sync-complete', { detail: { success: true } }));
                } else {
                    console.log('❌ Failed to store login data in extension');
                    // Show error message to the user
                    toast.error('Failed to sync with Instavault extension');
                    // Dispatch custom event to notify sync completion
                    window.dispatchEvent(new CustomEvent('instavault-extension-sync-complete', { detail: { success: false } }));
                }

                // Clean up the listener
                window.removeEventListener('message', handleExtensionResponse);
            }
        };

        window.addEventListener('message', handleExtensionResponse);

        // Set a timeout in case the extension doesn't respond
        setTimeout(() => {
            window.removeEventListener('message', handleExtensionResponse);
            console.log('⚠️ Extension did not respond within timeout');
            // Dispatch custom event to notify sync completion with failure
            window.dispatchEvent(new CustomEvent('instavault-extension-sync-complete', { detail: { success: false } }));
        }, 5000);
    } else {
        console.log('ℹ️ Instavault extension not detected');
    }
}

// Function to trigger Chrome bookmark sync via the extension
export function syncChromeBookmarks(): Promise<boolean> {
    return new Promise((resolve) => {
        if (typeof window === 'undefined' || !isInstavaultExtensionLoaded()) {
            toast.error('Instavault extension not detected. Please install it first.');
            resolve(false);
            return;
        }

        window.postMessage({ action: 'syncChromeBookmarks', source: 'instavault-website' }, '*');

        const handleResponse = (event: MessageEvent) => {
            if (event.data && event.data.action === 'bookmarkSyncStarted' && event.data.source === 'instavault-extension') {
                window.removeEventListener('message', handleResponse);
                clearTimeout(timeout);
                if (event.data.success) {
                    toast.success('Chrome bookmark sync started!');
                    resolve(true);
                } else {
                    toast.error('Failed to start bookmark sync.');
                    resolve(false);
                }
            }
        };

        window.addEventListener('message', handleResponse);

        const timeout = setTimeout(() => {
            window.removeEventListener('message', handleResponse);
            toast.error('Extension did not respond. Please try again.');
            resolve(false);
        }, 5000);
    });
}

// Function to get extension status for UI display
export function getExtensionStatus() {
    if (typeof window === 'undefined') return 'unknown';

    if (isInstavaultExtensionLoaded()) {
        return 'loaded';
    }

    return 'not-loaded';
}
